import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, School, Save, Send, EyeOff } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";

const JOB_STATUS = { none: "offen", applied: "Beworben", interview: "Gespräch", offer: "Zusage", reject: "Absage" };

export function WbClassLinks({ classId }) {
  if (!classId) return null;
  return (
    <div className="flex gap-2 flex-wrap" data-testid="wb-akte-class-links">
      <Link to={`/weiterbildung/chat?class=${classId}`} className="px-3 py-2 text-xs border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid="wb-akte-chat-link"><MessageCircle size={12} /> Klassenchat</Link>
      <Link to={`/weiterbildung/klasse/${classId}`} className="px-3 py-2 text-xs border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid="wb-akte-classroom-link"><School size={12} /> Klassenraum</Link>
    </div>
  );
}

export function WbCalendar({ cls }) {
  if (!cls) return <div className="text-sm text-slate-500" data-testid="wb-akte-calendar">Keiner Klasse zugeordnet.</div>;
  const plan = cls.weekly_plan || [];
  return (
    <div className="border border-slate-200 bg-white" data-testid="wb-akte-calendar">
      <div className="px-5 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500">Wochenplan · {cls.name} · {formatDeShort(cls.start_date)} – {formatDeShort(cls.end_date)}</div>
      {plan.length === 0 ? <div className="p-5 text-sm text-slate-500">Für diese Klasse ist noch kein Wochenplan hinterlegt.</div> : (
        <ul className="divide-y divide-slate-100">{plan.map((w, i) => (
          <li key={i} className="px-5 py-3 text-sm flex gap-4 flex-wrap">
            <span className="font-mono text-xs text-slate-400 w-44 shrink-0">KW {w.week_number ?? i + 1} · {formatDeShort(w.start_date)}–{formatDeShort(w.end_date)}</span>
            <span className="text-slate-900 min-w-0">{w.theme || "—"}{(w.topics || []).length > 0 && <span className="block text-xs text-slate-500">{w.topics.join(" · ")}</span>}</span>
          </li>))}
        </ul>)}
    </div>
  );
}

export function WbJobs({ pid }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/wb-akte/${pid}/jobs`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [pid]);
  if (!rows) return <div className="text-xs text-slate-400">Lädt…</div>;
  return (
    <div className="border border-slate-200 bg-white" data-testid="wb-akte-jobs">
      <div className="px-5 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500">Bewerbungen · {rows.length}</div>
      {rows.length === 0 ? <div className="p-5 text-sm text-slate-500">Der Teilnehmer hat noch keine Bewerbungen eingetragen.</div> : (
        <ul className="divide-y divide-slate-100">{rows.map((j, i) => (
          <li key={j.job_id || i} className="px-5 py-3 text-sm flex items-center gap-3">
            <div className="flex-1 min-w-0"><div className="text-slate-900">{j.function} · {j.company}</div><div className="text-[11px] text-slate-500">{j.applied_on ? `beworben am ${formatDeShort(j.applied_on)}` : "noch nicht beworben"}</div></div>
            <span className="px-2 py-0.5 text-[11px] bg-slate-100 text-slate-700">{JOB_STATUS[j.status] || j.status}</span>
          </li>))}
        </ul>)}
    </div>
  );
}

export function WbReport({ pid, kind, label }) {
  const [r, setR] = useState(null);
  const [text, setText] = useState("");
  useEffect(() => {
    api.get(`/wb-akte/${pid}/reports`).then((res) => { setR(res.data[kind] || {}); setText(res.data[kind]?.content || ""); }).catch(() => setR({}));
  }, [pid, kind]);
  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
  const save = async () => {
    try { const { data } = await api.put(`/wb-akte/${pid}/reports/${kind}`, { content: text }); setR(data); toast.success(`${label} gespeichert`); } catch (e) { err(e); }
  };
  const release = async (on) => {
    try {
      await api.put(`/wb-akte/${pid}/reports/${kind}`, { content: text });
      const { data } = await api.post(`/wb-akte/${pid}/reports/${kind}/release?released=${on}`);
      setR(data);
      toast.success(on ? "Für den Teilnehmer freigegeben" : "Freigabe zurückgenommen");
    } catch (e) { err(e); }
  };
  if (!r) return <div className="text-xs text-slate-400">Lädt…</div>;
  return (
    <div className="border border-slate-200 bg-white p-5 space-y-3" data-testid={`wb-report-${kind}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-heading text-xl font-light tracking-tighter text-slate-900">{label}</div>
        <div className="text-xs text-slate-500" data-testid={`wb-report-${kind}-status`}>{r.released ? `Freigegeben am ${formatDeShort(r.released_at)}` : r.updated_at ? `Entwurf · gespeichert ${formatDeShort(r.updated_at)}` : "Noch nicht angelegt"}</div>
      </div>
      <textarea rows={14} value={text} onChange={(e) => setText(e.target.value)} className="input-tech resize-y" placeholder={`${label} für den Teilnehmer verfassen…`} data-testid={`wb-report-${kind}-text`} />
      <div className="flex gap-2 flex-wrap">
        <button onClick={save} className="px-4 py-2 text-xs border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid={`wb-report-${kind}-save`}><Save size={12} /> Speichern</button>
        {r.released
          ? <button onClick={() => release(false)} className="px-4 py-2 text-xs border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid={`wb-report-${kind}-unrelease`}><EyeOff size={12} /> Freigabe zurücknehmen</button>
          : <button onClick={() => release(true)} disabled={!text.trim()} className="px-4 py-2 text-xs bg-slate-900 text-white hover:bg-black disabled:opacity-40 flex items-center gap-1.5" data-testid={`wb-report-${kind}-release`}><Send size={12} /> Für Teilnehmer freigeben</button>}
      </div>
    </div>
  );
}

export function MyWbReports() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/weiterbildung/me/reports").then((r) => setRows(r.data)).catch(() => {}); }, []);
  if (!rows.length) return null;
  return (
    <div className="space-y-4 mb-8" data-testid="participant-reports">
      {rows.map((r) => (
        <div key={r.kind} className="border border-slate-200 bg-white p-6" data-testid={`participant-report-${r.kind}`}>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">Freigegeben am {formatDeShort(r.released_at)}</div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900 mb-3">{r.kind === "final" ? "Abschlussbericht" : "Zwischenbericht"}</div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{r.content}</div>
        </div>
      ))}
    </div>
  );
}
